import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import VerificationInput from "react-verification-input";
import { jwtDecode, JwtPayload } from "jwt-decode";
import useLang from "../../../Shared/hooks/useLanguage";
import Commonservice from "Services/CommonService";
import {
  setLabType,
  setRefreshToken,
  setTokenData,
  setUserInfo,
  setWebInfo,
} from "Redux/Actions/Index";
import {
  setFaviconAndTitle,
  setValueIntoSessionStorage,
} from "Utils/Common/CommonMethods";
import { Encrypt, getTokenData } from "Utils/Auth";
import { IApiResponse } from "Interface/Shared/ApiResponse";
import AccountService from "Services/Account/AccountService";
import useAuth from "Shared/hooks/useAuth";
import { getFingerprint } from "Utils/Common/HelperFunction";

interface CustomJwtPayload extends JwtPayload {
  Lab_Classification: string;
}

const TRUSTED_DEVICE_DAYS = 5;

const AuthenticatorVerificationCode = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLang();
  const dispatch = useDispatch();
  const { labId, userId } = useParams();
  const [logoUrl, setLogoUrl] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [deviceId, setDeviceId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const { navigateUserBasedOnTenant } = useAuth();
  const verificationInputRef = useRef<any>(null);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handleBackButton = () => {
      navigate("/login", { state: { ...location?.state } });
    };
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, [location?.state, navigate]);

  useEffect(() => {
    if (verificationInputRef.current) {
      verificationInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (otp === "" && verificationInputRef.current) {
      verificationInputRef.current.focus();
    }
  }, [otp]);

  const getLogoUrl = () => {
    Commonservice.getLoginPageLogo()
      .then((res: any) => {
        setLogoUrl(res.data.logo);
        dispatch(
          setWebInfo({
            smartLogoUrl: res.data?.smartLogo,
            title: res.data?.labName,
          })
        );
        setFaviconAndTitle(res.data?.smartLogo, res.data?.labName);
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  useEffect(() => {
    const loadFingerprint = async () => {
      const id = await getFingerprint();
      setDeviceId(id);
    };
    loadFingerprint();
  }, []);

  useLayoutEffect(() => {
    getLogoUrl();
  }, []);

  const handleCancel = () => {
    navigate(`/authentication/${labId}/${userId}`, {
      state: {
        labId,
        userId,
        mfaTrustedDeviceDays: location?.state?.mfaTrustedDeviceDays,
        username: location?.state?.username,
        password: location?.state?.password,
        portalKey: location?.state?.portalKey,
      },
    });
  };

  const handleSubmit = async () => {
    if (otp.length !== 6) {
      toast.error(t("Please enter a valid 6-digit code."));
      return;
    }

    if (!deviceId) {
      toast.error(t("Device verification is still loading. Please try again."));
      return;
    }

    setLoading(true);

    const trustedDays = trustDevice ? TRUSTED_DEVICE_DAYS : 0;

    const loginRequest = {
      userId: userId,
      labId: Number(labId),
      code: otp,
      deviceId: deviceId,
      trustedDays,
      MfaType: "Authenticator",
    };

    const portalKey = location?.state?.portalKey;

    try {
      const res: IApiResponse<any> = await AccountService.userLogin(
        loginRequest,
        portalKey
      );

      if (res.data.status === 400) {
        toast.error(res.data.message);
        setOtp("");
        return;
      }

      if (res.data.isNewUser) {
        navigate("/security-questions-username", {
          state: { userId: res.data.userId, username: res.data.username },
        });
        return;
      }

      const data = res.data;
      const encryptData: any = Encrypt(JSON.stringify(res.data));
      const decodedToken: CustomJwtPayload = jwtDecode(data.token);

      sessionStorage.setItem("userinfo", encryptData);

      dispatch(setLabType(decodedToken?.Lab_Classification));
      dispatch(setRefreshToken(data.refreshToken));
      dispatch(setUserInfo(encryptData));

      if (window.location.pathname === "/externallogin") {
        setValueIntoSessionStorage("externalLogin", "true");
      }

      navigateUserBasedOnTenant(data);
      const TokenDetails = getTokenData();
      dispatch(setTokenData(TokenDetails));

      if (process.env.NODE_ENV === "production" && res.status === 200) {
        toast.success(t("Logged In!"));
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          t("An unexpected error occurred. Please try again.")
      );
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={{
        backgroundImage: `url('${process.env.PUBLIC_URL}/media/illustrations/login/sketch-1.png')`,
      }}
    >
      <div
        className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20"
        style={{ minHeight: "100vh" }}
      >
        <div className="w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto">
          {logoUrl && (
            <a href="#" className="mb-12 d-flex justify-content-center">
              <img alt="Logo" src={logoUrl} className="h-45px" />
            </a>
          )}

          <div className="text-center mb-10">
            <h4 className="fw-bold">{t("Enter Verification Code")}</h4>
            <p>
              {t("Please enter the 6-digit code from your Authenticator app.")}
            </p>
          </div>

          <div className="fv-row mb-10 d-flex justify-content-center">
            <VerificationInput
              ref={verificationInputRef}
              autoFocus
              length={6}
              validChars="0-9"
              placeholder=""
              onChange={(val: string) => setOtp(val)}
              value={otp}
              inputProps={{ inputMode: "numeric" }}
              classNames={{
                container: "d-flex gap-2",
                character: "form-control form-control-lg text-center",
                characterInactive: "opacity-50",
              }}
            />
          </div>

          <div className="form-check form-check-inline form-check-solid m-0 d-flex align-items-center gap-2 mt-4 mb-6">
            <input
              className="form-check-input h-15px w-15px rounded-01 m-0"
              type="checkbox"
              id="trustDeviceCheckboxAuthenticatorMFA"
              onChange={(e: any) => setTrustDevice(e.target.checked)}
              checked={trustDevice}
            />
            <label
              className="form-check-label text-muted"
              htmlFor="trustDeviceCheckboxAuthenticatorMFA"
              style={{
                fontSize: "0.85rem",
                lineHeight: "1.2",
              }}
            >
              {t("Trust this device for")} {TRUSTED_DEVICE_DAYS} {t("days")}{" "}
              <span className="d-none d-sm-inline">
                ({t("skip verification next time")})
              </span>
            </label>
          </div>

          <div className="d-flex gap-3">
            <button
              type="button"
              className="btn btn-secondary w-50 h-40px"
              onClick={handleCancel}
              disabled={loading}
              id="mfaAuthenticatorCancelButton"
            >
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary w-50 h-40px"
              onClick={handleSubmit}
              disabled={loading || otp.length !== 6}
              id="mfaAuthenticatorSubmitButton"
            >
              {loading ? t("Submitting...") : t("Submit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticatorVerificationCode;
